import type { PayBasis } from '../api/schemas';
import { formatMoneyCents } from './format';
import { dollarsToCents } from './money';
import { periodFor, type ReportPeriod } from './reports';

/**
 * Finanzas > Reportes > Planilla: the quincenas a studio pays by, and how a
 * rate and a duration read. Days are Panama calendar days (`YYYY-MM-DD`),
 * the calendar the API counts classes in, so the 31st at 8 p. m. is still
 * the 31st. Money goes to the server as integer cents, never a float.
 */

export type PayrollHalf = 'primera' | 'segunda';
export type PayrollMonth = 'actual' | 'anterior';
export type PayrollChoice = { half: PayrollHalf; month: PayrollMonth };

export const PAYROLL_HALVES: readonly { id: PayrollHalf; label: string }[] = [
  { id: 'primera', label: '1 al 15' },
  { id: 'segunda', label: '16 a fin de mes' },
];

/** The API refuses longer windows (`PAYROLL_MAX_RANGE_DAYS` in services/api). */
export const PAYROLL_MAX_RANGE_DAYS = 62;
/** $1,000,000 per class or per hour: the ceiling the API and the database hold. */
export const MAX_PAY_RATE_CENTS = 100_000_000;

const FIRST_HALF_LAST_DAY = 15;
const DAY_MS = 86_400_000;
const MONTH_NAME = new Intl.DateTimeFormat('es-PA', { month: 'long', timeZone: 'UTC' });
const MINUTES_PER_HOUR = 60;

export const RATE_FORMAT_MESSAGE = 'Escribe una tarifa válida, por ejemplo 25.00.';
export const RATE_TOO_HIGH_MESSAGE = `La tarifa máxima es ${formatMoneyCents(MAX_PAY_RATE_CENTS)}.`;

const BASIS_WORDS: Readonly<Record<PayBasis, string>> = { class: 'por clase', hour: 'por hora' };

type YearMonth = { year: number; month: number };

function todayKey(now: Date): string {
  return periodFor('hoy', now).from;
}

function yearMonthOf(key: string): YearMonth {
  return { year: Number(key.slice(0, 4)), month: Number(key.slice(5, 7)) };
}

function previousMonth({ year, month }: YearMonth): YearMonth {
  return month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
}

/** `month` is 1-based; day 0 of the next month is the last day of this one. */
function lastDayOf({ year, month }: YearMonth): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function dayKey({ year, month }: YearMonth, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function chosenMonth(month: PayrollMonth, now: Date): YearMonth {
  const current = yearMonthOf(todayKey(now));
  return month === 'actual' ? current : previousMonth(current);
}

/** "1 al 15" or "16 a fin de mes" of this month or the one before. */
export function payrollPeriod(half: PayrollHalf, month: PayrollMonth, now: Date): ReportPeriod {
  const target = chosenMonth(month, now);
  if (half === 'primera') return { from: dayKey(target, 1), to: dayKey(target, FIRST_HALF_LAST_DAY) };
  return { from: dayKey(target, FIRST_HALF_LAST_DAY + 1), to: dayKey(target, lastDayOf(target)) };
}

/**
 * The latest quincena whose cut-off is today or earlier: what the owner is
 * about to pay. The 10th opens on the second half of last month, the 15th to
 * the 30th on the first half of this one, the last day on the second half.
 */
export function defaultPayrollChoice(now: Date): PayrollChoice {
  const today = todayKey(now);
  const day = Number(today.slice(8, 10));
  if (day === lastDayOf(yearMonthOf(today))) return { half: 'segunda', month: 'actual' };
  if (day >= FIRST_HALF_LAST_DAY) return { half: 'primera', month: 'actual' };
  return { half: 'segunda', month: 'anterior' };
}

function monthWord(target: YearMonth): string {
  return MONTH_NAME.format(new Date(Date.UTC(target.year, target.month - 1, 1, 12)));
}

/** "Octubre": the month a choice points at, for the month switch. */
export function monthName(month: PayrollMonth, now: Date): string {
  const word = monthWord(chosenMonth(month, now));
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** "Corte: 15 de octubre". */
export function cutoffLabel(cutoff: string): string {
  return `Corte: ${Number(cutoff.slice(8, 10))} de ${monthWord(yearMonthOf(cutoff))}`;
}

/** "1 h 45 min", "50 min", "2 h": whole minutes, the unit the API counts in. */
export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  const rest = minutes % MINUTES_PER_HOUR;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/** "$25.00 por clase", or "Sin tarifa" when none is on file. */
export function rateLabel(rateCents: number | null, basis: PayBasis | null): string {
  if (rateCents === null || basis === null) return 'Sin tarifa';
  return `${formatMoneyCents(rateCents)} ${BASIS_WORDS[basis]}`;
}

export type RateInputResult = { ok: true; cents: number } | { ok: false; message: string };

/** What the rate field sends: whole cents within the server's ceiling, or the one message to show. */
export function parseRateInput(raw: string): RateInputResult {
  const cents = dollarsToCents(raw);
  if (cents === undefined) return { ok: false, message: RATE_FORMAT_MESSAGE };
  if (cents > MAX_PAY_RATE_CENTS) return { ok: false, message: RATE_TOO_HIGH_MESSAGE };
  return { ok: true, cents };
}

/** The API's window rules, said before it is asked. */
export function payrollRangeProblem(range: ReportPeriod): string | undefined {
  if (!range.from || !range.to) return 'Elige las dos fechas.';
  if (range.from > range.to) return 'La fecha de inicio va antes que la final.';
  const days = Math.round((Date.parse(range.to) - Date.parse(range.from)) / DAY_MS) + 1;
  if (days > PAYROLL_MAX_RANGE_DAYS) return `La planilla cubre hasta ${PAYROLL_MAX_RANGE_DAYS} días.`;
  return undefined;
}
